import { finalize, takeUntil } from "rxjs/operators";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router, ActivatedRoute } from "@angular/router";
import { Statement } from "../statement";
import { StatementService } from "../statement.service";
import { IdentityVerificationService, IdentityRefusalReason } from "./identity-verification.service";
import { IdentityVerificationResetDialogComponent } from "app/common/identity-verification-reset-dialog/identity-verification-reset-dialog.component";
import { SubscribingComponent } from "../../common";
import { ErrorDialogComponent } from "../../common/error-dialog/error-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { PackageOptions } from "../package-options";
import { ONFIDO_STATUS_CHECK_LIMIT } from "../../common/settings";
import { ONFIDO_REFRESH_STATUS_INTERVAL } from "../../common/settings";
import { FinishPagePath } from "app/core/workflow/page-path/statement-page-path/finish-page.path";
import { ThankYouComponentPath } from "app/core/workflow/page-path/statement-page-path/thank-you-component.path";
import { DashboardPagePath } from "app/core/workflow/page-path/dashboard-page.path";
import { LanguageSelectService } from "app/core/services/language-select.service";
import { lastValueFrom } from "rxjs";
import snsWebSdk from "@sumsub/websdk";
import type SnsWebSdk from "@sumsub/websdk/types/SnsWebSdk";

@Component({
  selector: "app-identity-verification",
  templateUrl: "./identity-verification.component.html",
  styleUrls: ["./identity-verification.component.scss"],
  standalone: false
})
export class IdentityVerificationComponent extends SubscribingComponent implements OnInit, OnDestroy {
  public isVeryfying = false;
  public isIdentityVarificationActive = false;
  public statementId: number;
  public statement: Statement;
  private identificationStatusCheckLimit = 0;
  private statusPollTimeout: ReturnType<typeof setTimeout> | null = null;
  private snsWebSdkInstance: SnsWebSdk | null = null;
  private awaitingUserResubmission = false;
  public cantGetOnfidoStatusCheck = false;
  public canSendWithTaxandoCert = false;
  public canSendWithTaxandoCertActive = true;
  public packageOption = PackageOptions;
  public isLoading = true;

  constructor(
    private statementService: StatementService,
    private identityVerificationService: IdentityVerificationService,
    private router: Router,
    private route: ActivatedRoute,
    public readonly languageSelectService: LanguageSelectService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {
    super();
    this.onVerificationComplete = this.onVerificationComplete.bind(this);
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      const statementId = parseInt(params["statementId"], 10);

      this.statementId = statementId;
    });

    this.getStatementData(this.statementId);
  }

  ngOnDestroy(): void {
    this.clearStatusPoll();
    this.destroySdk();
    super.ngOnDestroy();
  }

  startVerification(): void {
    this.resetVerificationSession();
    this.isIdentityVarificationActive = true;
    this.requestVerificationTokenAndLaunchSdk();
  }

  private restartVerificationForResubmission(): void {
    this.resetVerificationSession();
    this.awaitingUserResubmission = true;
    this.isVeryfying = false;
    this.requestVerificationTokenAndLaunchSdk();
  }

  private resetVerificationSession(): void {
    this.clearStatusPoll();
    this.identificationStatusCheckLimit = 0;
    this.cantGetOnfidoStatusCheck = false;
    this.isVeryfying = false;
  }

  private destroySdk(): void {
    this.snsWebSdkInstance?.destroy();
    this.snsWebSdkInstance = null;
  }

  private requestVerificationTokenAndLaunchSdk(): void {
    this.identityVerificationService
      .initOnfidoVerification(this.statementId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          this.launchIdentityVerificationSDK(response.onfido_token);
        },
        (error) => {
          this.handleInitVerificationRefusal(error);
        }
      );
  }

  public launchIdentityVerificationSDK(accessToken: string): void {
    this.destroySdk();

    this.snsWebSdkInstance = snsWebSdk
      .init(accessToken, () => this.getNewAccessToken())
      .withConf({
        lang: this.choseLanguage(), //language of WebSDK texts and comments (ISO 639-1 format)
        theme: "light"
      })
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on("idCheck.onApplicantSubmitted", () => {
        if (!this.awaitingUserResubmission) {
          this.onVerificationComplete();
        }
      })
      .on("idCheck.onApplicantResubmitted", () => {
        this.awaitingUserResubmission = false;
        this.onVerificationComplete();
      })
      .on("idCheck.onError", (error) => {
        // Sumsub renders its own error screen inside the iframe.
        console.warn("Sumsub onError", error);
      })
      .build();

    this.snsWebSdkInstance.launch("#sumsub-websdk-container");
  }

  public async getNewAccessToken(): Promise<string> {
    try {
      const response = await lastValueFrom(
        this.identityVerificationService.initOnfidoVerification(this.statementId).pipe(takeUntil(this.ngUnsubscribe))
      );
      return response.onfido_token;
    } catch (error) {
      this.handleInitVerificationRefusal(error);
      throw error;
    }
  }

  private handleInitVerificationRefusal(error: any): void {
    const { reason, resets_remaining, statement_id } = error.error ?? {};

    switch (reason) {
      case IdentityRefusalReason.RedFinal:
        if (resets_remaining !== null && resets_remaining > 0) {
          this.showResetDialog(resets_remaining);
        } else {
          this.showContactErrorDialog();
        }
        break;
      case IdentityRefusalReason.Verified:
        this.redirectToThankYou(statement_id ?? this.statementId);
        break;
      case IdentityRefusalReason.Pending:
        this.showPendingVerificationState();
        break;
      default:
        this.showContactErrorDialog();
        break;
    }
  }

  private showContactErrorDialog(): void {
    this.translate
      .get("ONFIDO.FAILED_VERIFICATION")
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((message: string) => this.showErrorDialog(message, true));
  }

  private showResetDialog(resetsRemaining: number): void {
    this.destroySdk();
    this.clearStatusPoll();
    this.awaitingUserResubmission = false;
    this.isVeryfying = false;

    this.dialog
      .open(IdentityVerificationResetDialogComponent, {
        data: { resetsRemaining },
        panelClass: "identity-verification-reset-dialog"
      })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.confirmIdentityVerificationReset();
        } else {
          this.router.navigate([DashboardPagePath.fullUrl()]);
        }
      });
  }

  private confirmIdentityVerificationReset(): void {
    this.identityVerificationService
      .resetIdentityVerification(this.statementId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          if (response.ok) {
            this.resetVerificationSession();
            this.isIdentityVarificationActive = true;
            this.requestVerificationTokenAndLaunchSdk();
            return;
          }
          this.showContactErrorDialog();
        },
        error: () => {
          this.showContactErrorDialog();
        }
      });
  }

  private redirectToThankYou(statementId: number): void {
    this.destroySdk();
    this.clearStatusPoll();
    this.awaitingUserResubmission = false;
    this.isVeryfying = false;
    this.router.navigate([ThankYouComponentPath.fullUrl(statementId)]);
  }

  private showPendingVerificationState(): void {
    this.destroySdk();
    this.clearStatusPoll();
    this.awaitingUserResubmission = false;
    this.isIdentityVarificationActive = true;
    this.isVeryfying = false;
    this.onVerificationComplete();
  }

  choseLanguage() {
    const chosenLang = this.languageSelectService.getLanguage();

    if (chosenLang == "ua") return "uk"; //TAXANDO handle Ukrainian as UA, sumsub i using UK

    return chosenLang;
  }

  onVerificationComplete(): void {
    if (this.isVeryfying) {
      return;
    }
    this.isVeryfying = true;
    this.identificationStatusCheckLimit = 0;
    this.cantGetOnfidoStatusCheck = false;
    this.observeIdentityVerificationProcess();
  }

  observeIdentityVerificationProcess(): void {
    this.identityVerificationService
      .checkOnfidoVerification(this.statementId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          const status = response["is_verified"];
          const resubmission_required = response["resubmission_required"] || false;
          if (resubmission_required) {
            this.restartVerificationForResubmission();
          } else {
            this.checkIdentityVerificationStatus(status);
          }
        },
        (error: any) => {
          const message = error.error;
          this.showErrorDialog(message);
        }
      );
  }

  checkIdentityVerificationStatus(status) {
    if (status === true) {
      this.clearStatusPoll();
      if (this.statement.serviceType == PackageOptions.tax_adviser) {
        this.statementService.updateStatementStatus(this.statementId).subscribe(() => {
          this.router.navigate([FinishPagePath.fullUrl(this.statementId)]);
        });
      } else {
        this.router.navigate([FinishPagePath.fullUrl(this.statementId)]);
      }
    } else if (status === false) {
      this.clearStatusPoll();
      this.isVeryfying = false;
      // A live SDK shows the rejection screen itself; without one (pending on
      // re-entry) init routes the verdict to the reset or contact dialog.
      if (!this.snsWebSdkInstance) {
        this.requestVerificationTokenAndLaunchSdk();
      }
    } else if (this.identificationStatusCheckLimit <= ONFIDO_STATUS_CHECK_LIMIT) {
      this.identificationStatusCheckLimit++;
      this.scheduleStatusPoll();
    } else {
      this.cantGetOnfidoStatusCheck = true;
    }
  }

  private scheduleStatusPoll(): void {
    this.clearStatusPoll();
    this.statusPollTimeout = setTimeout(() => {
      this.statusPollTimeout = null;
      if (this.isVeryfying) {
        this.observeIdentityVerificationProcess();
      }
    }, ONFIDO_REFRESH_STATUS_INTERVAL);
  }

  private clearStatusPoll(): void {
    if (this.statusPollTimeout !== null) {
      clearTimeout(this.statusPollTimeout);
      this.statusPollTimeout = null;
    }
  }

  showErrorDialog(message, hideOnfidoButton = false) {
    this.clearStatusPoll();
    this.awaitingUserResubmission = false;
    if (hideOnfidoButton) {
      this.canSendWithTaxandoCert = false;
    }
    if (message && typeof message === "string") {
      const dialogRef = this.dialog.open(ErrorDialogComponent);
      dialogRef.componentInstance.message = message;
      dialogRef.componentInstance.showEmail = false;
    }
    this.isVeryfying = false;
  }

  getStatementData(id: number) {
    this.isLoading = true;
    this.statementService
      .getStatement(id)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.isLoading = false;
          this.startVerification();
        })
      )
      .subscribe(
        (response: Statement) => {
          this.statement = response;
        },
        () => this.router.navigate(["/new"])
      );
  }
}
